import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import { telegramAdminService } from '@/services/telegram-admin-service';

const formSchema = z.object({
  adminId: z.string().min(1, 'Admin is required'),
  message: z.string().min(1, 'Message is required'),
  parseMode: z.enum(['Markdown', 'HTML', 'none']).default('none'),
});

type FormData = z.infer<typeof formSchema>;

interface SendTestMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SendTestMessageDialog({
  open,
  onOpenChange,
}: SendTestMessageDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: admins, isLoading: isLoadingAdmins } = useQuery({
    queryKey: ['telegram-admins-all'],
    queryFn: () => telegramAdminService.getAdminUsers(1, 100, { status: true }),
    enabled: open,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      adminId: '',
      message: '',
      parseMode: 'none',
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const selectedAdmin = admins?.data?.find((a: any) => a.id === data.adminId);
      if (!selectedAdmin) {
        throw new Error('Admin not found');
      }

      await telegramAdminService.sendTestMessage(
        selectedAdmin.telegram_id,
        data.message,
        data.parseMode !== 'none' ? data.parseMode : undefined
      );

      toast.success('Test message sent successfully');
      form.reset();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(`Failed to send message: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send Test Message</DialogTitle>
          <DialogDescription>
            Send a test message to any registered admin user
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="adminId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Select Admin</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose an admin" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingAdmins ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          Loading admins...
                        </div>
                      ) : admins?.data && admins.data.length > 0 ? (
                        admins.data.map((admin: any) => (
                          <SelectItem key={admin.id} value={admin.id}>
                            {admin.telegram_first_name} (@{admin.telegram_username || 'N/A'})
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-muted-foreground">
                          No active admins found
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter your test message here..."
                      rows={5}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="parseMode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Parse Mode</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select parse mode" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="Markdown">Markdown</SelectItem>
                      <SelectItem value="HTML">HTML</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Message
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
